import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import api from '../services/api.ts'
import ReportContent, { ReportMetrics } from '../components/ReportContent.tsx'

interface Company {
  id: string
  name: string
}

const SECTIONS = [
  {
    value: 'kpis',
    label: 'KPI Summary',
    description: 'ROI, Revenue, Churn, LTV, Conversion Rate, New Signups',
  },
  {
    value: 'funnel',
    label: 'Lead → Customer Funnel',
    description:
      'New Leads > SPS Booked > SPS Complete > New Members > $ Revenue (Lead Journey pipeline)',
  },
  {
    value: 'leads',
    label: 'Lead Analytics',
    description: 'New contacts by source, won opportunities',
  },
  {
    value: 'opportunities',
    label: 'Opportunities',
    description: 'Pipeline stages, stage breakdown',
  },
  {
    value: 'calls',
    label: 'Incoming Calls',
    description: 'Call status, first-time callers, qualified rate',
  },
  { value: 'members', label: 'Current Members', description: 'Member count and source breakdown' },
  {
    value: 'contacts',
    label: 'Contact List',
    description: 'All leads with name, phone, email, source, tags',
  },
  {
    value: 'costs',
    label: 'Costs & Profit',
    description: 'Management fee, ad spend, profit breakdown',
  },
]

const SetupReport: React.FC = () => {
  const navigate = useNavigate()
  const { reportId } = useParams<{ reportId: string }>()
  const isEditing = !!reportId

  const [error, setError] = useState<string | null>(null)
  const [companies, setCompanies] = useState<Company[]>([])
  const [loadingCompanies, setLoadingCompanies] = useState(true)
  const [loadingReport, setLoadingReport] = useState(isEditing)
  const [previewData, setPreviewData] = useState<ReportMetrics | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewError, setPreviewError] = useState<string | null>(null)

  useEffect(() => {
    api
      .get('/api/companies')
      .then(res => {
        setCompanies(res.data.companies || [])
      })
      .catch(() => {
        setError('Failed to load companies. Please refresh and try again.')
      })
      .finally(() => setLoadingCompanies(false))
  }, [])

  const formik = useFormik({
    initialValues: {
      name: '',
      cadence: 'monthly' as 'daily' | 'weekly' | 'monthly',
      companyId: '',
      sections: [
        'kpis',
        'funnel',
        'leads',
        'opportunities',
        'calls',
        'members',
        'contacts',
      ] as string[],
      dateFrom: '',
      dateTo: '',
    },
    validationSchema: Yup.object({
      name: Yup.string().min(1).max(100).required('Report name is required'),
      cadence: Yup.string().oneOf(['daily', 'weekly', 'monthly']).required('Cadence is required'),
      companyId: Yup.string().required('Please select a company'),
      sections: Yup.array().of(Yup.string()).min(1, 'Select at least one section'),
      dateFrom: Yup.string().test(
        'date-pair',
        'Both start and end dates are required',
        function (value) {
          const { dateTo } = this.parent
          if ((!value && !dateTo) || (value && dateTo)) return true
          return false
        },
      ),
      dateTo: Yup.string()
        .test('date-pair', 'Both start and end dates are required', function (value) {
          const { dateFrom } = this.parent
          if ((!value && !dateFrom) || (value && dateFrom)) return true
          return false
        })
        .test('date-order', 'End date must be after start date', function (value) {
          const { dateFrom } = this.parent
          if (!dateFrom || !value) return true
          return new Date(value) >= new Date(dateFrom)
        }),
    }),
    onSubmit: async values => {
      setError(null)
      const payload = {
        name: values.name,
        cadence: values.cadence,
        companyId: values.companyId,
        configJson: {
          sections: values.sections,
          dateRange:
            values.dateFrom && values.dateTo
              ? { from: values.dateFrom, to: values.dateTo }
              : undefined,
        },
      }
      try {
        if (isEditing) {
          await api.put(`/api/reports/${reportId}`, payload)
        } else {
          await api.post('/api/reports', payload)
        }
        navigate('/reports')
      } catch (err: any) {
        setError(
          err?.response?.data?.message || `Failed to ${isEditing ? 'update' : 'create'} report`,
        )
      }
    },
  })

  // Load existing report for editing
  useEffect(() => {
    if (!reportId) return
    api
      .get(`/api/reports/${reportId}`)
      .then(res => {
        const r = res.data.report
        const config = r.config_json || {}
        formik.setValues({
          name: r.name,
          cadence: r.cadence,
          companyId: r.company_id || '',
          sections: config.sections || ['kpis', 'leads', 'opportunities', 'members'],
          dateFrom: config.dateRange?.from || '',
          dateTo: config.dateRange?.to || '',
        })
      })
      .catch(() => {
        setError('Failed to load report')
      })
      .finally(() => setLoadingReport(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId])

  const toggleSection = (sectionValue: string) => {
    const current = formik.values.sections
    if (current.includes(sectionValue)) {
      formik.setFieldValue(
        'sections',
        current.filter(s => s !== sectionValue),
      )
    } else {
      formik.setFieldValue('sections', [...current, sectionValue])
    }
  }

  const handlePreview = async () => {
    if (!formik.values.companyId) return
    setPreviewLoading(true)
    setPreviewError(null)
    setPreviewData(null)
    try {
      const params: Record<string, string> = {}
      if (formik.values.dateFrom) params.date_from = formik.values.dateFrom
      if (formik.values.dateTo) params.date_to = formik.values.dateTo
      const sections = formik.values.sections
      if (sections.includes('calls') || sections.length === 0) params.include_calls = 'true'
      if (sections.includes('contacts') || sections.length === 0) params.include_contacts = 'true'
      const res = await api.get(`/api/company/${formik.values.companyId}/lead-analytics`, {
        params,
      })
      // The endpoint returns { success, period, kpis, ... } — strip `success` to get ReportMetrics
      const { success: _success, ...metrics } = res.data
      setPreviewData(metrics as ReportMetrics)
    } catch (err: any) {
      setPreviewError(err?.response?.data?.message || 'Failed to load preview data')
    } finally {
      setPreviewLoading(false)
    }
  }

  if (loadingCompanies || loadingReport) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div
        className={`${previewData ? 'max-w-6xl' : 'max-w-2xl'} mx-auto transition-all duration-300`}
      >
        <div className="text-center mb-8">
          <h2 className="text-3xl font-extrabold text-gray-900 mb-2">
            {isEditing ? 'Edit Report' : 'Setup New Report'}
          </h2>
          <p className="text-gray-600">
            {isEditing
              ? 'Update your report configuration'
              : 'Configure your automated report settings'}
          </p>
        </div>

        <div className="bg-white shadow-xl rounded-lg border border-gray-200 p-8">
          <form onSubmit={formik.handleSubmit} className="space-y-6">
            {/* Report Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                Report Name
              </label>
              <input
                id="name"
                name="name"
                type="text"
                className={`block w-full px-3 py-3 border ${
                  formik.touched.name && formik.errors.name ? 'border-red-300' : 'border-gray-300'
                } text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                placeholder="e.g. Monthly Performance Report"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.name}
              />
              {formik.touched.name && formik.errors.name && (
                <p className="mt-1 text-sm text-red-600">{formik.errors.name}</p>
              )}
            </div>

            {/* Company Selector */}
            <div>
              <label htmlFor="companyId" className="block text-sm font-medium text-gray-700 mb-2">
                Company
              </label>
              <select
                id="companyId"
                name="companyId"
                className={`block w-full px-3 py-3 border ${
                  formik.touched.companyId && formik.errors.companyId
                    ? 'border-red-300'
                    : 'border-gray-300'
                } text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.companyId}
              >
                <option value="">Select a company</option>
                {companies.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
              {formik.touched.companyId && formik.errors.companyId && (
                <p className="mt-1 text-sm text-red-600">{formik.errors.companyId}</p>
              )}
            </div>

            {/* Cadence */}
            <div>
              <label htmlFor="cadence" className="block text-sm font-medium text-gray-700 mb-2">
                Report Cadence
              </label>
              <select
                id="cadence"
                name="cadence"
                className="block w-full px-3 py-3 border border-gray-300 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.cadence}
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>

            {/* Date Range */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date Range{' '}
                <span className="text-gray-400 font-normal">
                  (optional — defaults to current month)
                </span>
              </label>
              <div className="grid grid-cols-2 gap-4">
                <input
                  type="date"
                  name="dateFrom"
                  className="block w-full px-3 py-3 border border-gray-300 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  onChange={formik.handleChange}
                  value={formik.values.dateFrom}
                />
                <input
                  type="date"
                  name="dateTo"
                  className="block w-full px-3 py-3 border border-gray-300 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  onChange={formik.handleChange}
                  value={formik.values.dateTo}
                />
              </div>
              {((formik.touched.dateFrom && formik.errors.dateFrom) ||
                (formik.touched.dateTo && formik.errors.dateTo)) && (
                <p className="mt-1 text-sm text-red-600">
                  {formik.errors.dateFrom || formik.errors.dateTo}
                </p>
              )}
            </div>

            {/* Report Sections */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Report Sections
              </label>
              <div className="space-y-3">
                {SECTIONS.map(section => (
                  <label
                    key={section.value}
                    className={`flex items-start p-3 border rounded-lg cursor-pointer transition-colors ${
                      formik.values.sections.includes(section.value)
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={formik.values.sections.includes(section.value)}
                      onChange={() => toggleSection(section.value)}
                      className="mt-0.5 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-900">{section.label}</p>
                      <p className="text-xs text-gray-500">{section.description}</p>
                    </div>
                  </label>
                ))}
              </div>
              {formik.touched.sections && formik.errors.sections && (
                <p className="mt-1 text-sm text-red-600">{formik.errors.sections}</p>
              )}
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            <div className="flex justify-end space-x-4 pt-6">
              <button
                type="button"
                onClick={() => navigate('/reports')}
                className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handlePreview}
                disabled={!formik.values.companyId || previewLoading}
                className={`px-6 py-3 border text-sm font-medium rounded-lg ${
                  !formik.values.companyId || previewLoading
                    ? 'border-gray-300 text-gray-400 cursor-not-allowed'
                    : 'border-blue-300 text-blue-600 hover:bg-blue-50 transition-colors'
                }`}
              >
                {previewLoading ? (
                  <span className="flex items-center">
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Loading...
                  </span>
                ) : (
                  'Preview Report'
                )}
              </button>
              <button
                type="submit"
                disabled={formik.isSubmitting || !formik.isValid}
                className={`px-6 py-3 border border-transparent text-sm font-medium rounded-lg text-white ${
                  formik.isSubmitting || !formik.isValid
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700 transition-colors'
                }`}
              >
                {formik.isSubmitting ? 'Saving...' : isEditing ? 'Update Report' : 'Create Report'}
              </button>
            </div>
          </form>
        </div>

        {/* Preview Area */}
        {previewError && (
          <div className="mt-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-sm text-red-700">{previewError}</p>
          </div>
        )}

        {previewData && (
          <div className="mt-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Report Preview</h3>
              <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded">
                Live data preview
              </span>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
              <ReportContent sections={formik.values.sections} data={previewData} />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default SetupReport
